import { Diagram } from '../types';

// Shared "hub" layout: three nodes across the top, the hub in the middle, two nodes at the bottom.
// Node boxes are 120×56; the hub is 204×108 centred on x=240.
const TOP = { left: { x: 16, y: 24 }, mid: { x: 180, y: 24 }, right: { x: 344, y: 24 } };
const BOTTOM = { left: { x: 16, y: 260 }, right: { x: 344, y: 260 } };
const HUB = { x: 138, y: 128, w: 204, h: 108 };

const EDGE = {
  topLeftToMid: 'M136 52 H180',
  topMidToRight: 'M300 52 H344',
  topMidToHub: 'M240 80 V128',
  hubToTopRight: 'M342 160 H404 V80',
  hubToTopLeft: 'M138 160 H76 V80',
  hubToBottomLeft: 'M138 210 H76 V260',
  hubToBottomRight: 'M342 210 H404 V260',
};

// Packet routes; (240, 182) is the hub centre and (76|404, 52|288) are node centres.
const ROUTE = {
  topLeftToHub: 'M76 52 H240 V182',
  hubToTopRight: 'M240 182 V160 H404 V52',
  hubToTopLeft: 'M240 182 V160 H76 V52',
  hubToBottomLeft: 'M240 182 V210 H76 V288',
  hubToBottomRight: 'M240 182 V210 H404 V288',
  bottomRightToHub: 'M404 288 V210 H240 V182',
};

export const executorDiagram: Diagram = {
  repo: 'remote-code-executor',
  caption: 'request path',
  status: 'running',
  ariaLabel: 'Client to API gateway to SQS queue to a sandboxed worker pool, with DynamoDB for state and S3 for result logs',
  height: 330,
  nodes: [
    { x: 16, y: 24, title: 'client', sub: 'POST /submit' },
    { x: 180, y: 24, title: 'api-gateway', sub: 'go · nginx' },
    { x: 344, y: 24, title: 'aws sqs', sub: 'job queue' },
    { x: 16, y: 128, title: 'dynamodb', sub: 'execution state' },
    { x: 16, y: 260, title: 'aws s3', sub: 'result logs' },
  ],
  hub: {
    x: 260,
    y: 128,
    w: 204,
    h: 108,
    title: 'worker pool',
    chips: ['go-worker', 'cpp-worker'],
    notes: ['docker · 256MB · 1 cpu · 64 pids', 'no-net · read-only fs · non-root'],
  },
  edges: [
    { d: 'M136 52 H180' },
    { d: 'M300 52 H344', label: { x: 322, y: 44, text: 'enqueue' } },
    { d: 'M404 80 V128', label: { x: 428, y: 108, text: 'poll' } },
    { d: 'M260 200 H198 V288 H136', label: { x: 226, y: 250, text: 'logs' } },
    { d: 'M240 80 V156 H136', label: { x: 176, y: 148, text: 'state' } },
  ],
  packets: [
    { path: 'M76 52 H404 V200 H198 V288 H76', dur: '5s' },
    { path: 'M76 52 H404 V200 H198 V288 H76', dur: '5s', begin: '-2.5s' },
    { path: 'M240 52 V156 H76', dur: '3.2s', begin: '-1s' },
  ],
  logs: [
    'job 7f3a  go   exit 0   212ms   1.4 KB',
    'job 7f3b  cpp  exit 0   486ms   3.1 KB',
    'job 7f3c  go   TLE      2000ms  killed',
    'job 7f3d  cpp  exit 1   97ms    stderr → s3',
  ],
};

export const foresightDiagram: Diagram = {
  repo: 'foresight',
  caption: 'safety loop',
  status: 'offline-ready',
  ariaLabel: 'A 10 Hz telemetry simulator feeds a FastAPI process whose safety engine sends alerts to the cab UI, appends to a hash-chained incident log and serves a voice agent',
  height: 330,
  nodes: [
    { ...TOP.left, title: 'telemetry', sub: '10 Hz simulator' },
    { ...TOP.mid, title: 'fastapi', sub: 'one process' },
    { ...TOP.right, title: 'cab ui', sub: '/ws/live' },
    { ...BOTTOM.left, title: 'incident log', sub: 'sha-256 chain' },
    { ...BOTTOM.right, title: 'voice agent', sub: 'groq → ollama' },
  ],
  hub: {
    ...HUB,
    title: 'safety engine',
    chips: ['rules', 'xgboost'],
    notes: ['ml can raise, never lower', '4.6 ms p50 per decision'],
  },
  edges: [
    { d: EDGE.topLeftToMid, label: { x: 158, y: 44, text: 'frames' } },
    { d: EDGE.topMidToHub },
    { d: EDGE.hubToTopRight, label: { x: 428, y: 120, text: 'alerts' } },
    { d: EDGE.hubToBottomLeft, label: { x: 104, y: 240, text: 'append' } },
    { d: EDGE.hubToBottomRight, label: { x: 378, y: 240, text: 'voice' } },
  ],
  packets: [
    { path: ROUTE.topLeftToHub, dur: '2.4s' },
    { path: ROUTE.topLeftToHub, dur: '2.4s', begin: '-1.2s' },
    { path: ROUTE.hubToTopRight, dur: '3s', begin: '-0.8s' },
    { path: ROUTE.hubToBottomLeft, dur: '3s', begin: '-2s' },
    { path: ROUTE.bottomRightToHub, dur: '5s', begin: '-1s' },
  ],
  logs: [
    'tick 4812  clearance 6.2m  risk 0.12  ok',
    'tick 4813  worker closing  risk 0.58  warn',
    'alert  proximity → logged, chain verified',
    'voice  "dismiss that" → refused: hard alert',
  ],
};

export const roommateDiagram: Diagram = {
  repo: 'roommate-dhoondho',
  caption: 'request path',
  status: 'live',
  ariaLabel: 'React client calls an Express server through rate-limit and JWT middleware with Google OAuth; the server matches on MongoDB, pushes updates over Socket.IO and sends email via Brevo and Resend',
  height: 330,
  nodes: [
    { ...TOP.left, title: 'react client', sub: 'vercel' },
    { ...TOP.mid, title: 'middleware', sub: 'rate-limit · jwt' },
    { ...TOP.right, title: 'google oauth', sub: 'passport.js' },
    { ...BOTTOM.left, title: 'mongodb', sub: 'mongoose' },
    { ...BOTTOM.right, title: 'brevo · resend', sub: 'notifications' },
  ],
  hub: {
    ...HUB,
    title: 'express server',
    chips: ['matching', 'socket.io'],
    notes: ['location · lifestyle prefs', '400+ concurrent at peak'],
  },
  edges: [
    { d: EDGE.topLeftToMid, label: { x: 158, y: 44, text: 'https' } },
    { d: EDGE.topMidToRight, label: { x: 322, y: 44, text: 'oauth' } },
    { d: EDGE.topMidToHub },
    { d: EDGE.hubToTopLeft, label: { x: 100, y: 120, text: 'ws push' } },
    { d: EDGE.hubToBottomLeft, label: { x: 104, y: 240, text: 'queries' } },
    { d: EDGE.hubToBottomRight, label: { x: 378, y: 240, text: 'notify' } },
  ],
  packets: [
    { path: ROUTE.topLeftToHub, dur: '3.6s' },
    { path: ROUTE.hubToBottomLeft, dur: '3.6s', begin: '-1.2s' },
    { path: ROUTE.hubToTopLeft, dur: '3.6s', begin: '-2.4s' },
    { path: ROUTE.hubToBottomRight, dur: '5s', begin: '-0.6s' },
  ],
  logs: [
    'auth   google oauth → jwt issued',
    'match  prefs: quiet · early riser → 12 found',
    'ws     roommate request → pushed to recipient',
    'mail   request accepted → email sent',
  ],
};

export const leaderboardDiagram: Diagram = {
  repo: 'live-leaderboard',
  caption: 'score path',
  status: 'running',
  ariaLabel: 'A game server submits scores to an Express router; the leaderboard core updates Redis, broadcasts rankings over Socket.IO and flushes batched writes to Postgres, MySQL or MongoDB',
  height: 330,
  nodes: [
    { ...TOP.left, title: 'game server', sub: 'POST /scores' },
    { ...TOP.mid, title: 'express router', sub: 'drop-in rest api' },
    { ...TOP.right, title: 'socket.io', sub: 'broadcast top-n' },
    { ...BOTTOM.left, title: 'redis', sub: 'live ranks' },
    { ...BOTTOM.right, title: 'persistence', sub: 'pg | mysql | mongo' },
  ],
  hub: {
    ...HUB,
    title: 'leaderboard core',
    chips: ['validate', 'rank'],
    notes: ['write-behind batching', 'idx (game_id, score desc)'],
  },
  edges: [
    { d: EDGE.topLeftToMid, label: { x: 158, y: 44, text: 'submit' } },
    { d: EDGE.topMidToHub },
    { d: EDGE.hubToTopRight, label: { x: 428, y: 120, text: 'emit' } },
    { d: EDGE.hubToBottomLeft, label: { x: 104, y: 240, text: 'update' } },
    { d: EDGE.hubToBottomRight, label: { x: 378, y: 240, text: 'flush' } },
  ],
  packets: [
    { path: ROUTE.topLeftToHub, dur: '3.4s' },
    { path: ROUTE.hubToBottomLeft, dur: '3.4s', begin: '-1.1s' },
    { path: ROUTE.hubToTopRight, dur: '3.4s', begin: '-2.2s' },
    { path: ROUTE.hubToBottomRight, dur: '6s', begin: '-0.5s' },
  ],
  logs: [
    'POST /scores  game=arena  user=u_42  +120',
    'redis  rank(u_42) → #3',
    'emit   leaderboard:arena  top-10',
    'flush  48 writes → postgres (batched)',
  ],
};
